

















































import Vue from "vue";
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";
import { IPartnerImage } from "@/models/partner-image.entity";
import { IVideo, Video } from "@/models/video.entity";
import { formatBytes } from "@/lib/utility/file-helper";
import { MediaGalleryItemType } from "./MediaGalleryFullscreen.vue";
import videoService from "@/services/mrfiktiv/services/video.service";
import { handleError } from "@/lib/utility/handleError";

@Component
class MediaGalleryItem extends Vue {
  @Prop({ required: true }) value!: IPartnerImage | IVideo;
  @Prop({ required: true }) type!: MediaGalleryItemType;

  /**
   * Images dont have upload progress, so we lie to our users.
   */
  fakeProgress = 0;

  videoEndpoint: string | null = null;
  videoProgress: number | null = null;
  private videoInCreation: IVideo | null = null;
  private fakeProgressInterval: number | null = null;

  private videoHydrateTimeout: ReturnType<typeof setTimeout> | null = null;

  get isImage() {
    return this.type === MediaGalleryItemType.IMAGE;
  }

  get isImageUpload() {
    if (this.type !== MediaGalleryItemType.IMAGE) return false;
    const img = this.value;
    return img?._file instanceof File;
  }

  get isVideoUpload() {
    if (this.type !== MediaGalleryItemType.VIDEO) return false;
    const vid = this.value;
    return vid?._file instanceof File;
  }

  get showUploadProgress() {
    return this.isImageUpload || this.isVideoUpload;
  }

  get title() {
    if (this.type === MediaGalleryItemType.IMAGE) {
      const img = this.value as IPartnerImage;
      return img.name || img.id || "";
    } else {
      const vid = this.value as IVideo;
      return vid.title || vid.id || "";
    }
  }

  get detail() {
    if (this.type === MediaGalleryItemType.IMAGE) {
      const img = this.value as IPartnerImage;
      const bytes = img?.metaData?.size;
      return typeof bytes === "number" ? formatBytes(bytes) : "";
    }
    const vid = this.value as IVideo;
    const duration = vid?.asset?.duration;
    if (typeof duration === "number" && isFinite(duration) && duration > 0) {
      const total = Math.round(duration);
      const m = Math.floor(total / 60);
      const s = total % 60;
      return `${m}:${String(s).padStart(2, "0")}`;
    }
    return "";
  }

  get imageUrl() {
    return (this.value as IPartnerImage)?.url || "";
  }

  get progress() {
    if (this.isImageUpload) return this.fakeProgress;
    if (this.isVideoUpload) return this.videoProgress;
    return undefined;
  }

  get progressValue(): number | null | undefined {
    return this.progress;
  }

  async mounted() {
    if (this.isImageUpload) {
      await this.startImageUpload();
    }

    if (this.isVideoUpload) {
      await this.startVideoUpload();
    }
  }

  beforeDestroy() {
    if (this.fakeProgressInterval != null) {
      window.clearInterval(this.fakeProgressInterval);
      this.fakeProgressInterval = null;
    }

    this.clearHydateTimeout();

    if (this.isVideoUpload) {
      const uploader = this.$refs.uploader as (HTMLElement & { upload?: { abort?: () => void } }) | undefined;
      uploader?.upload?.abort?.();
    }
  }

  async startVideoUpload() {
    try {
      await import("@mux/mux-uploader");

      const partnerId = this.value.partnerId;
      if (!partnerId) {
        throw new Error("Missing partnerId for video upload");
      }

      const file = this.value?._file as File | undefined;
      if (!(file instanceof File)) {
        throw new Error("Missing file for video upload");
      }

      const created = await videoService.create(partnerId, {});
      const video = new Video(created);
      this.videoInCreation = video;

      if (!video.uploadUrl) throw new Error("Missing Mux upload URL");
      this.videoEndpoint = video.uploadUrl;

      // ensure mux-uploader exists with the endpoint
      await this.$nextTick();

      const uploader = this.$refs.uploader as (HTMLElement & { upload?: { abort?: () => void } }) | undefined;
      if (!uploader) throw new Error("Mux uploader not available");

      // Show indeterminate until we get real progress.
      this.videoProgress = null;

      uploader.dispatchEvent(
        new CustomEvent("file-ready", {
          detail: file,
          composed: true,
          bubbles: true
        })
      );
    } catch (e) {
      handleError(e);
      this.$emit("error", e);
    }
  }

  async startImageUpload() {
    try {
      if (this.isImageUpload) {
        this.fakeProgress = 0;
        this.fakeProgressInterval = window.setInterval(() => {
          this.fakeProgress = Math.min(95, this.fakeProgress + 10);
        }, 500);
      }

      const partnerImage = this.value as IPartnerImage;
      const created = await partnerImage.create();
      this.fakeProgress = 100;

      if (this.fakeProgressInterval != null) {
        window.clearInterval(this.fakeProgressInterval);
        this.fakeProgressInterval = null;
      }

      this.$emit("created", created.id);
    } catch (e) {
      handleError(e);
      this.$emit("error", e);
    }
  }

  onMuxProgress(e: { detail: number }) {
    this.videoProgress = e?.detail ?? 0;
  }

  async onMuxSuccess() {
    try {
      const video = this.videoInCreation;
      if (!video) throw new Error("Video missing after upload");

      await this.hydrateVideo(video);
    } catch (e) {
      handleError(e);
      this.$emit("error", e);
    }
  }

  clearHydateTimeout() {
    if (this.videoHydrateTimeout != null) {
      clearTimeout(this.videoHydrateTimeout);
      this.videoHydrateTimeout = null;
    }
  }

  private async hydrateVideo(video: IVideo, timeoutCount = 0, startedAt = Date.now()): Promise<void> {
    if (!this.isVideoUpload) return;
    this.clearHydateTimeout();

    await video.fetch().catch(e => this.$log.error(e));

    if (video?.hasAsset && video?.canPlayback) {
      this.$emit("created", video.id);
      return;
    }

    const maxPostProcessingMs = 2 * 60 * 1000;
    if (Date.now() - startedAt >= maxPostProcessingMs) {
      const error = new Error("Video post-processing timed out");
      this.$emit("error", error);
      return;
    }
    this.videoHydrateTimeout = setTimeout(() => {
      this.hydrateVideo(video, timeoutCount + 1, startedAt);
    }, 1000 * (timeoutCount + 1));
  }

  onMuxError(e: any) {
    handleError(e);
    this.$emit("error", e);
  }
}

export default MediaGalleryItem;
